import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, Droplet, Trash } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCostingMovements,
  useDeleteCostingMovement,
} from "~/hooks/costing";
import { useToast } from "~/hooks/use-toast";
import type { CostingMovement } from "~/types/costing";

import {
  ITEM_KIND_LABELS,
  MOVEMENT_CLASS_LABELS,
  PeriodSelector,
  fmtCLP,
  fmtQty,
  oilLedgerRowToKg,
  useCanEditCosting,
  useCosteoPeriod,
  useOilDensities,
} from "./components/CosteoShared";

export function CosteoConsumosPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const canEdit = useCanEditCosting();

  const { periods, period, periodRow, setPeriod } = useCosteoPeriod();
  const { data: movements = [], isLoading } = useCostingMovements(
    periodRow?.id,
    {
      movementClass: ["consumption", "waste", "adjustment"],
    }
  );

  const { data: densities } = useOilDensities();

  const deleteMovement = useDeleteCostingMovement();

  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState<CostingMovement | null>(null);

  const isOpen = periodRow?.status === "open";

  // Manual entry targets ingredients only (finished-good adjustments live in Conciliación).
  const ingredientMovements = useMemo(
    () => movements.filter((m) => m.item_kind !== "finished_good"),
    [movements]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ingredientMovements;
    return ingredientMovements.filter((m) => {
      const code = m.raw_materials?.code ?? m.oil_types?.code ?? "";
      const name = m.raw_materials?.name ?? m.oil_types?.name ?? "";
      return (
        code.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        (m.document_number ?? "").toLowerCase().includes(q)
      );
    });
  }, [ingredientMovements, search]);

  // Aceites shown in kg (liters × density); the $ total is unchanged.
  const displayMovements = useMemo(
    () =>
      filtered.map((m) =>
        m.item_kind === "oil_type"
          ? oilLedgerRowToKg(m, densities?.[m.oil_type_id ?? ""])
          : m
      ),
    [filtered, densities]
  );

  const totalValue = useMemo(
    () => filtered.reduce((s, m) => s + Math.abs(Number(m.total_cost_clp)), 0),
    [filtered]
  );

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMovement.mutateAsync(deleting.id);
      toast({ title: "Movimiento eliminado" });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/production/batches")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Droplet className="h-6 w-6" />
            Consumos de Materia Prima
          </h1>
          <p className="text-muted-foreground">
            Consumos según recetas de los lotes de producción, valorizados al
            costo promedio del período
          </p>
        </div>
        <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por código, ítem o lote…"
          className="h-9 max-w-sm"
        />
      </div>

      <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Fecha</TableHead>
              <TableHead>Ítem</TableHead>
              <TableHead className="w-32">Lote / Doc.</TableHead>
              <TableHead className="w-28">Clase</TableHead>
              <TableHead className="text-right">Cantidad</TableHead>
              <TableHead className="text-right">Costo Unit. (WAC)</TableHead>
              <TableHead className="text-right">Total</TableHead>
              {canEdit && isOpen && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-muted-foreground"
                >
                  Sin consumos registrados en {period || "este período"}
                </TableCell>
              </TableRow>
            ) : (
              displayMovements.map((m, index) => {
                const code = m.raw_materials?.code ?? m.oil_types?.code;
                const name = m.raw_materials?.name ?? m.oil_types?.name;
                const isManual = m.source_type === "manual";
                return (
                  <TableRow
                    key={m.id}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  >
                    <TableCell>
                      {dayjs(m.movement_date).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{name}</div>
                      <div className="text-xs text-muted-foreground">
                        {code} · {ITEM_KIND_LABELS[m.item_kind]}
                      </div>
                    </TableCell>
                    <TableCell>{m.document_number ?? "—"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          m.movement_class === "consumption"
                            ? "outline"
                            : "secondary"
                        }
                      >
                        {MOVEMENT_CLASS_LABELS[m.movement_class]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtQty(m.quantity)}
                    </TableCell>
                    <TableCell className="text-right">
                      {Number(m.unit_cost_clp) > 0 ? (
                        fmtCLP(m.unit_cost_clp)
                      ) : (
                        <span className="text-amber-600">pendiente</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {fmtCLP(m.total_cost_clp)}
                    </TableCell>
                    {canEdit && isOpen && (
                      <TableCell>
                        {isManual && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleting(m)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <div className="flex justify-end border-t bg-muted/30 px-4 py-2 text-sm">
            <span className="font-medium">
              Valor total: {fmtCLP(totalValue)} ({filtered.length} movimientos)
            </span>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar movimiento manual</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMovement.isPending}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
